import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Wrench, Megaphone, Receipt, ShieldAlert, CalendarDays, ArrowUpRight, Sparkles } from "lucide-react";

function Kpi({ label, value, icon: Icon, tone = "primary", testid, to }) {
  const tones = {
    primary: "bg-primary/15 text-gold",
    secondary: "bg-secondary/10 text-secondary",
    danger: "bg-destructive/10 text-destructive",
    warning: "bg-[hsl(32_87%_62%/0.15)] text-[hsl(32_60%_35%)]",
  };
  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${tones[tone]}`}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        {to && <ArrowUpRight className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="text-3xl font-semibold tracking-tight font-mono text-gold" data-testid={`${testid}-value`}>
        {value}
      </div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="hoa-card hoa-card-hover p-6 block" data-testid={testid}>
        {body}
      </Link>
    );
  }
  return (
    <div className="hoa-card p-6" data-testid={testid}>
      {body}
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="hoa-card hoa-card-hover p-6 flex items-start justify-between group"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div>
          <div className="font-semibold text-foreground">{title}</div>
          <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const isBoard = user?.role === "admin" || user?.role === "board";

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/announcements"),
        ]);
        setStats(s.data);
        setAnnouncements(a.data.slice(0, 3));
      } catch (err) {
        console.error("Dashboard load failed:", err);
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
          <Sparkles className="w-4 h-4" />
          {isBoard ? "Board control center" : "Your community"}
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="dashboard-greeting">
          Welcome back, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-foreground/70 mt-2">
          {isBoard
            ? "Here's what needs your attention today."
            : "Everything about your unit and community, in one place."}
        </p>
      </div>

      {/* KPIs */}
      {stats && isBoard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="board-kpis">
          <Kpi label="Outstanding dues" value={`$${(stats.outstanding_dues || 0).toFixed(0)}`} icon={Receipt} tone="danger" testid="kpi-dues" to="/delinquency" />
          <Kpi label="Open work orders" value={stats.open_work_orders ?? 0} icon={Wrench} tone="warning" testid="kpi-wo" to="/work-orders" />
          <Kpi label="Open violations" value={stats.open_violations ?? 0} icon={ShieldAlert} tone="secondary" testid="kpi-viol" to="/violations" />
          <Kpi label="Upcoming meetings" value={stats.upcoming_meetings ?? 0} icon={CalendarDays} tone="primary" testid="kpi-meet" to="/meetings" />
        </div>
      )}

      {stats && !isBoard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="owner-kpis">
          <Kpi label="Your balance" value={`$${(stats.my_balance || 0).toFixed(2)}`} icon={Receipt} tone={stats.my_balance > 0 ? "danger" : "primary"} testid="kpi-balance" to="/my-balance" />
          <Kpi label="Your open requests" value={stats.my_open_work_orders ?? 0} icon={Wrench} tone="warning" testid="kpi-my-wo" to="/work-orders" />
          <Kpi label="Your open violations" value={stats.my_open_violations ?? 0} icon={ShieldAlert} tone="secondary" testid="kpi-my-viol" to="/violations" />
          <Kpi label="Upcoming meetings" value={stats.upcoming_meetings ?? 0} icon={CalendarDays} tone="primary" testid="kpi-meet" to="/meetings" />
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-xl font-semibold tracking-tight mb-4">
          {isBoard ? "Next best actions" : "Do it now"}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {isBoard ? (
            <>
              <QuickAction to="/work-orders" icon={Wrench} title="Review work orders" desc="Approve, update status, add notes." testid="qa-review-wo" />
              <QuickAction to="/announcements" icon={Megaphone} title="Post an announcement" desc="Share updates with all owners." testid="qa-post-announce" />
              <QuickAction to="/dues" icon={Receipt} title="Record dues activity" desc="Log a charge or payment for a unit." testid="qa-record-dues" />
              <QuickAction to="/violations" icon={ShieldAlert} title="Issue a violation" desc="Warn, escalate, or resolve." testid="qa-issue-viol" />
            </>
          ) : (
            <>
              <QuickAction to="/work-orders" icon={Wrench} title="Submit a work order" desc="Report a repair or maintenance issue." testid="qa-submit-wo" />
              <QuickAction to="/dues" icon={Receipt} title="View your dues" desc="See your balance & history." testid="qa-view-dues" />
              <QuickAction to="/announcements" icon={Megaphone} title="Community news" desc="Latest updates from the board." testid="qa-community-news" />
              <QuickAction to="/meetings" icon={CalendarDays} title="Upcoming meetings" desc="RSVP and read the agenda." testid="qa-meetings" />
            </>
          )}
        </div>
      </div>

      {/* Latest announcements */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold tracking-tight">Latest announcements</h2>
          <Link to="/announcements" className="text-sm text-gold font-medium hover:underline" data-testid="see-all-announcements">
            See all
          </Link>
        </div>
        <div className="space-y-3">
          {announcements.length === 0 && (
            <div className="hoa-card p-8 text-center text-muted-foreground" data-testid="no-announcements">
              No announcements yet.
            </div>
          )}
          {announcements.map((a) => (
            <div key={a.id} className="hoa-card p-5" data-testid={`announce-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                {a.pinned && <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full font-medium">Pinned</span>}
                <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
              <div className="font-semibold text-foreground">{a.title}</div>
              <p className="text-sm text-foreground/70 mt-1 line-clamp-2">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
